import React, { useMemo, useState } from "react";
import axios from "axios";
import ReactCountryFlag from "react-country-flag";
import { useQuery } from "@tanstack/react-query";

import { Button, Icon, IconButton, Money, SearchInput, Segmented, Select, StatePanel, Toggle } from "../../components/ui";
import { boxLabels, boxOptions, conditionLabels, conditionOptions, sourceOptions, yearOptions } from "../../product/constants";
import useTradeImage from "../../product/components/ProductPrices/useTradeImage";
import { AiModal } from "../../order/components/Quote/Leads";
import { regionFlag, regionLabel } from "./arbitrageRegions.js";
import "./arbitrage-live-data.scss";

/**
 * Live data under the arbitrage map (Alex, 2026-10-21): the watch's price
 * points, with who listed each — supplier, phone, business store, listing
 * code, link, and the WhatsApp photo for trade quotes.
 *
 *   Route prices  the exact price points behind the route selected on the
 *                 map: its buy leg cheapest first, its sell leg dearest
 *                 first. Picking another route on the globe or in the list
 *                 switches them.
 *   All prices    every price point of the reference, filtered and paged.
 *
 * Both read GET /api/products/{id}/live-prices[/routes]
 * (ProductLivePriceService), which reads prices the way the nightly
 * arbitrage run does, so a leg here holds the listings its route counted.
 */

const VIEWS = [
  { value: "routes", label: "Route prices" },
  { value: "all", label: "All prices" },
];

const WINDOWS = [
  { value: "1", label: "1M" },
  { value: "3", label: "3M" },
  { value: "6", label: "6M" },
  { value: "12", label: "12M" },
];

const ALL_WINDOWS = [...WINDOWS, { value: "", label: "All time" }];

const POOLS = [
  { value: "", label: "Trade + Market" },
  { value: "trade", label: "Trade" },
  { value: "market", label: "Market" },
];

const SORTS = [
  { value: "date_desc", label: "Newest first" },
  { value: "date_asc", label: "Oldest first" },
  { value: "price_asc", label: "Cheapest first" },
  { value: "price_desc", label: "Dearest first" },
];

const POOL_LABEL = { trade: "Trade", market: "Market" };

/** Points shown per leg before "Show all". */
const LEG_PREVIEW = 5;

const PER_PAGE = 25;

const sourceName = (id) => sourceOptions.find((o) => o.id === id)?.name || id || "—";
const toOptions = (list) => list.map((o) => ({ value: String(o.id), label: String(o.name) }));
/** "21 Oct 2026, 05:41" from "2026-10-21 05:41:12". */
const stamp = (s) => {
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime())
    ? String(s).slice(0, 16)
    : `${d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}, ${String(s).slice(11, 16)}`;
};
const shortDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "—";

const Flag = ({ region }) => {
  const code = regionFlag(region);
  return code ? <ReactCountryFlag countryCode={code} svg className="qld-flag" aria-hidden="true" /> : null;
};

/** One price point: photo, when, where, who (name · phone · store), what, how much, open. */
function LivePriceRow({ point, currency, onOpenTrade }) {
  const photo = useTradeImage(point);
  const details = [
    (point.condition || []).map((c) => conditionLabels[c] || c).join(", "),
    boxLabels[point.box],
    point.year_month?.slice(0, 4),
    point.availability,
  ].filter(Boolean);
  const foreign = point.currency && point.currency !== (currency || "gbp").toLowerCase();

  return (
    <li className="qld-row">
      <span className="qld-row__thumb">
        {photo.src ? (
          <img src={photo.src} alt="" loading="lazy" />
        ) : (
          <Icon name={point.pool === "trade" ? "MessageCircle" : "Globe"} size="sm" />
        )}
      </span>
      <span className="qld-row__main">
        <span className="qld-row__line">
          <span className={`qld-src is-${point.pool}`}>{sourceName(point.source)}</span>
          <span className="qld-row__where">
            <Flag region={point.region} />
            {point.region ? regionLabel(point.region) : "—"}
          </span>
          <span className="qld-row__date">{shortDate(point.price_date)}</span>
        </span>
        <span className="qld-row__who">
          <b>{point.supplier_name || "Unknown supplier"}</b>
          {point.supplier_phone && (
            <a href={`tel:${point.supplier_phone.replace(/\s+/g, "")}`} className="qld-row__phone">{point.supplier_phone}</a>
          )}
          {point.store_name && <span>{point.store_name}</span>}
          {point.wa_group && <span className="qld-row__muted">{point.wa_group}</span>}
        </span>
        <span className="qld-row__details">
          {details.join(" · ") || "—"}
          {point.listing_code && <span className="qld-row__code">#{point.listing_code}</span>}
        </span>
      </span>
      <span className="qld-row__price">
        <Money value={point.base_price} currency={currency} decimals={0} tone="strong" />
        {foreign && (
          <span className="qld-row__muted">
            <Money value={point.price} currency={point.currency} decimals={0} size="sm" />
          </span>
        )}
      </span>
      <span className="qld-row__open">
        {point.listing_link ? (
          <IconButton variant="ghost" size="sm" label="Open the listing" href={point.listing_link} target="_blank" rel="noopener noreferrer">
            <Icon name="ExternalLink" size="sm" />
          </IconButton>
        ) : point.pool === "trade" && point.listing_code ? (
          <IconButton variant="ghost" size="sm" label="Open the WhatsApp message" onClick={() => onOpenTrade(point)}>
            <Icon name="ExternalLink" size="sm" />
          </IconButton>
        ) : null}
      </span>
    </li>
  );
}

/** One side of the selected route: its points, cheapest first (buy) or dearest first (sell). */
function Leg({ side, leg, points, currency, onOpenTrade }) {
  const [all, setAll] = useState(false);
  const ordered = side === "buy" ? points : [...points].reverse();
  const shown = all ? ordered : ordered.slice(0, LEG_PREVIEW);
  return (
    <section className={`qld-leg is-${side}`}>
      <header className="qld-leg__head">
        <span className="qld-leg__side">{side === "buy" ? "Buy · cheapest first" : "Sell · dearest first"}</span>
        <span className="qld-leg__where">
          <Flag region={leg.region} /> {POOL_LABEL[leg.pool]} · {regionLabel(leg.region)}
        </span>
        {leg.median != null && (
          <span className="qld-leg__median">
            median <Money value={leg.median} currency={currency} decimals={0} size="sm" /> · {points.length} live price
            {points.length === 1 ? "" : "s"}
          </span>
        )}
      </header>
      {points.length === 0 ? (
        <p className="qld-muted">No price points in this period.</p>
      ) : (
        <ul className="qld-list">
          {shown.map((p) => <LivePriceRow key={p.id} point={p} currency={currency} onOpenTrade={onOpenTrade} />)}
        </ul>
      )}
      {points.length > LEG_PREVIEW && (
        <Button variant="ghost" size="sm" onClick={() => setAll((v) => !v)}>
          {all ? "Show fewer" : `Show all ${points.length}`}
        </Button>
      )}
    </section>
  );
}

function RoutePrices({ productId, storeIdM, initialMonths, focus, onOpenTrade }) {
  const [months, setMonths] = useState(initialMonths || "12");
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["live-prices-routes", storeIdM, productId, months],
    queryFn: async () =>
      (await axios.get(`/api/products/${productId}/live-prices/routes`, { params: { store_id_m: storeIdM, months } })).data,
    enabled: !!productId && storeIdM != null,
    staleTime: 60_000,
  });

  const currency = data?.base_currency || "gbp";
  const legs = data?.legs || {};
  const pointsOf = (leg) => legs[`${leg.pool}|${leg.region}`]?.points || [];

  return (
    <div className="qld-pane">
      <div className="qld-bar">
        <Segmented size="sm" label="Data period" options={WINDOWS} value={months} onChange={setMonths} />
        {data?.computed_at && <span className="qld-muted">Prices as of {stamp(data.computed_at)}</span>}
      </div>
      {!focus ? (
        <p className="qld-muted">Pick a route on the map or in the list to see its price points.</p>
      ) : isLoading ? (
        <StatePanel state="loading" compact title="Loading route prices…" />
      ) : isError ? (
        <StatePanel state="error" compact title="Couldn't load route prices" onRetry={() => refetch()} />
      ) : (
        <div className="qld-legs">
          <Leg side="buy" leg={focus.buy} points={pointsOf(focus.buy)} currency={currency} onOpenTrade={onOpenTrade} />
          <Leg side="sell" leg={focus.sell} points={pointsOf(focus.sell)} currency={currency} onOpenTrade={onOpenTrade} />
        </div>
      )}
    </div>
  );
}

function AllPrices({ productId, storeIdM, onOpenTrade }) {
  const [f, setF] = useState({
    months: "12", pool: "", sources: [], regions: [], condition: "", box: "", year: "", q: "",
    latest_only: true, sort: "date_desc", page: 1,
  });
  const [searchText, setSearchText] = useState("");
  // On a phone the filters fold behind one button; the search stays out.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const set = (patch) => setF((prev) => ({ ...prev, page: 1, ...patch }));

  const params = useMemo(() => {
    const p = { store_id_m: storeIdM, per_page: PER_PAGE, page: f.page, sort: f.sort, latest_only: f.latest_only ? 1 : 0 };
    ["months", "pool", "condition", "box", "year", "q"].forEach((k) => {
      if (f[k]) p[k] = f[k];
    });
    if (f.sources.length) p.sources = f.sources.join(",");
    if (f.regions.length) p.regions = f.regions.join(",");
    return p;
  }, [f, storeIdM]);

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ["live-prices-all", productId, params],
    queryFn: async () => (await axios.get(`/api/products/${productId}/live-prices`, { params })).data,
    enabled: !!productId && storeIdM != null,
    staleTime: 60_000,
    keepPreviousData: true,
  });

  const currency = data?.base_currency || "gbp";
  const points = data?.data || [];
  const sourceChoices = (data?.facets?.sources || []).map((s) => ({ value: s, label: sourceName(s) }));
  const regionChoices = (data?.facets?.regions || []).map((r) => ({ value: r, label: regionLabel(r) }));
  const activeFilters = [f.sources.length, f.regions.length, f.condition, f.box, f.year].filter(Boolean).length;

  return (
    <div className="qld-pane">
      <div className="qld-bar">
        <Segmented size="sm" label="Data period" options={ALL_WINDOWS} value={f.months} onChange={(months) => set({ months })} />
        <Segmented size="sm" label="Trade or market" options={POOLS} value={f.pool} onChange={(pool) => set({ pool })} />
        <Toggle size="sm" checked={f.latest_only} onChange={(latest_only) => set({ latest_only })} label="One per listing" />
      </div>
      <div className="qld-filters">
        <SearchInput value={searchText} onChange={(e) => setSearchText(e.target.value)}
          onSearch={(q) => q !== f.q && set({ q })} onClear={() => { setSearchText(""); set({ q: "" }); }}
          placeholder="Supplier, phone, store, listing code" size="md" />
        <button type="button" className="qld-filters__toggle" aria-expanded={filtersOpen} onClick={() => setFiltersOpen((v) => !v)}>
          <Icon name="Filter" size="sm" /> Filters{activeFilters ? ` (${activeFilters})` : ""}
        </button>
        <div className={`qld-filters__more${filtersOpen ? " is-open" : ""}`}>
        <Select label="Source" options={sourceChoices} value={f.sources} multi clearable placeholder="All sources"
          onChange={(sources) => set({ sources: sources || [] })} />
        <Select label="Region" options={regionChoices} value={f.regions} multi clearable placeholder="All regions"
          onChange={(regions) => set({ regions: regions || [] })} />
        <Select label="Condition" options={toOptions(conditionOptions)} value={f.condition} clearable placeholder="Any condition"
          onChange={(condition) => set({ condition: condition ?? "" })} />
        <Select label="Box & papers" options={toOptions(boxOptions)} value={f.box} clearable placeholder="Any set"
          onChange={(box) => set({ box: box ?? "" })} />
        <Select label="Year" options={toOptions(yearOptions.slice(0, 40))} value={f.year} clearable searchable placeholder="Any year"
          onChange={(year) => set({ year: year ?? "" })} />
        <Select label="Sort" options={SORTS} value={f.sort} onChange={(sort) => sort && set({ sort })} />
        </div>
      </div>

      {isLoading ? (
        <StatePanel state="loading" compact title="Loading prices…" />
      ) : isError ? (
        <StatePanel state="error" compact title="Couldn't load prices" onRetry={() => refetch()} />
      ) : points.length === 0 ? (
        <StatePanel state="empty" compact icon="Filter" title="No prices match these filters" />
      ) : (
        <>
          <p className="qld-muted" aria-live="polite">
            {data.total.toLocaleString()} price point{data.total === 1 ? "" : "s"}
            {data.truncated ? " · newest 5,000 read; narrow the period to see older ones" : ""}
          </p>
          <ul className={`qld-list${isFetching ? " is-refreshing" : ""}`}>
            {points.map((p) => <LivePriceRow key={p.id} point={p} currency={currency} onOpenTrade={onOpenTrade} />)}
          </ul>
          {data.last_page > 1 && (
            <nav className="qld-pager" aria-label="Pages">
              <Button variant="ghost" size="sm" disabled={f.page <= 1} onClick={() => setF((p) => ({ ...p, page: p.page - 1 }))}>Previous</Button>
              <span>Page {data.page} of {data.last_page}</span>
              <Button variant="ghost" size="sm" disabled={f.page >= data.last_page} onClick={() => setF((p) => ({ ...p, page: p.page + 1 }))}>Next</Button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

/**
 * @param {number} productId
 * @param {number} storeIdM
 * @param {string} months      the page's period, the starting window for Route prices
 * @param {{buy:{pool,region,median?}, sell:{pool,region,median?}}|null} focus   the route selected on the map
 */
export default function ArbitrageLiveData({ productId, storeIdM, months, focus }) {
  const [view, setView] = useState("routes");
  const [tradeItem, setTradeItem] = useState(null);
  const openTrade = (p) => setTradeItem({ product_id: productId, message_id: p.listing_code, messageStatus: "approved" });

  return (
    <section className="qld" aria-label="Live data">
      <div className="qld-head">
        <h3>Live data</h3>
        <Segmented size="sm" label="Live data view" options={VIEWS} value={view} onChange={setView} />
      </div>
      {view === "routes" ? (
        <RoutePrices productId={productId} storeIdM={storeIdM} initialMonths={months} focus={focus} onOpenTrade={openTrade} />
      ) : (
        <AllPrices productId={productId} storeIdM={storeIdM} onOpenTrade={openTrade} />
      )}
      <AiModal isOpen={!!tradeItem} item={tradeItem} hide={() => setTradeItem(null)} hideImport />
    </section>
  );
}
